import React from 'react';
import CorrectIcon from './CorrectIcon';
import WrongIcon from './WrongIcon';
import { useQuestionCardStyle } from '../contexts/QuestionCardStyleContext';

interface QuestionCardProps {
  points: number | string;
  earnedPoints?: number;
  children: React.ReactNode;
  className?: string;
  hidePointsIcon?: boolean;
  questionNumber?: number;
  isDailyDouble?: boolean;
  isCorrect?: boolean;
  'data-testid'?: string;
  'data-test-type'?: string;
}

/**
 * QuestionCard - Common wrapper for all question types
 * Provides consistent layout with points badge and card styling
 */
const QuestionCard: React.FC<QuestionCardProps> = ({
  points,
  earnedPoints,
  children,
  className = '',
  hidePointsIcon = false,
  questionNumber,
  isDailyDouble = false,
  isCorrect,
  'data-testid': dataTestId,
  'data-test-type': dataTestType,
}) => {
  const {
    bgGradientStart, bgGradientEnd, borderColor, questionNumberBg, questionNumberFg,
    ddCardGradientStart, ddCardGradientEnd, ddCardBorderColor, ddNumberBadgeBg, ddNumberBadgeFg,
    ddBackgroundImage, ddLogoImage, ddSecondaryImage,
  } = useQuestionCardStyle();
  const gradientStyle: React.CSSProperties =
    !isDailyDouble && (bgGradientStart || bgGradientEnd)
      ? {
          background: `linear-gradient(180deg, ${bgGradientStart ?? bgGradientEnd}, ${bgGradientEnd ?? bgGradientStart})`,
        }
      : {};

  const dynamicBorderStyle: React.CSSProperties =
    !isDailyDouble && borderColor ? { borderColor } : {};

  const pointsDisplay = () => {
    if (earnedPoints !== undefined) {
      // Results mode - show earned/potential format
      const potentialPoints = typeof points === 'number' ? points : parseInt(String(points));
      return `${earnedPoints}/${potentialPoints} PTS`;
    }
    // Selection mode - show just potential points
    return typeof points === 'number' ? `${points} PTS` : points;
  };

  const cardBackground = isDailyDouble
    ? 'bg-gradient-to-r from-[#011125] via-[#02295A] to-[#014296]'
    : 'bg-[#242424]';

  const cardBorder = isDailyDouble ? 'border-[#0D54AD]' : 'border-[#373737]';

  const numberBackground = isDailyDouble ? 'bg-[#E31937]' : 'bg-white';
  const numberTextColor = isDailyDouble ? 'text-white' : 'text-gray-900';

  const ddCardStyle: React.CSSProperties = isDailyDouble
    ? {
        ...(ddCardGradientStart || ddCardGradientEnd
          ? { background: `linear-gradient(to right, ${ddCardGradientStart ?? ddCardGradientEnd}, ${ddCardGradientEnd ?? ddCardGradientStart})` }
          : {}),
        ...(ddCardBorderColor ? { borderColor: ddCardBorderColor } : {}),
      }
    : {};

  return (
    <div
      className={`relative ${cardBackground} border ${cardBorder} rounded-xl p-2 md:p-4 mb-0 ${className}`}
      style={{ ...gradientStyle, ...dynamicBorderStyle, ...ddCardStyle }}
      data-testid={dataTestId}
      data-test-type={dataTestType}
    >
      {/* Background image overlay for Daily Double */}
      {isDailyDouble && (
        <div
          className="absolute inset-0 rounded-xl pointer-events-none"
          style={{
            backgroundImage: `url(${ddBackgroundImage ?? '/clouds.png'})`,
            backgroundSize: '100% auto',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'top center',
            opacity: 0.2,
          }}
        />
      )}
      {/* Question Number Circle - Top Center */}
      {!hidePointsIcon && questionNumber && (
        <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
          <div className="relative">
            <div
              className={`w-[24px] h-[24px] ${numberBackground} rounded-full flex items-center justify-center border-[2px] border-app-background relative z-10`}
              style={
                isDailyDouble
                  ? (ddNumberBadgeBg ? { backgroundColor: ddNumberBadgeBg } : {})
                  : (questionNumberBg ? { backgroundColor: questionNumberBg } : {})
              }
            >
              <span
                className={`${numberTextColor} font-gilroy font-bold text-sm leading-none`}
                style={
                  isDailyDouble
                    ? (ddNumberBadgeFg ? { color: ddNumberBadgeFg } : {})
                    : (questionNumberFg ? { color: questionNumberFg } : {})
                }
              >
                {questionNumber}
              </span>
            </div>
            {/* Bottom semi-circle border */}
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 w-[25px] h-[14px] overflow-hidden"
              style={{ zIndex: 5 }}
            >
              <div
                className={`w-[25px] h-[25px] rounded-full border-[2px] ${cardBorder} -translate-y-1/2`}
                style={isDailyDouble ? (ddCardBorderColor ? { borderColor: ddCardBorderColor } : {}) : dynamicBorderStyle}
              />
            </div>
          </div>
        </div>
      )}

      {/* Secondary image - Top Left (50% outside) for Daily Double */}
      {isDailyDouble && (
        <div className="absolute left-[25%] top-0 -translate-y-1/2 -translate-x-1/2 z-10">
          <img
            src={ddSecondaryImage ?? '/plane.png'}
            alt="Secondary"
            className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 object-contain"
            data-testid="daily-double-plane"
          />
        </div>
      )}

      {/* Daily Double Image - Above question content */}
      {isDailyDouble && (
        <div className="flex justify-center mb-4 mt-2">
          <img
            src={ddLogoImage ?? '/dailydouble.png'}
            alt="Daily Double"
            className="h-12 w-auto object-contain"
            data-testid="daily-double-image"
          />
        </div>
      )}

      {/* Question Content */}
      {children}

      {/* Points Display - Bottom Center. Tight against the options on phones, where
          card height is the scarce resource; the original spacing stays from md up. */}
      {!hidePointsIcon && (
        <div className="flex justify-center items-center gap-2 mt-1.5 md:mt-4">
          {isCorrect !== undefined && (
            isCorrect ? <CorrectIcon /> : <WrongIcon />
          )}
          <span className="text-[#ABABAB] font-gilroy text-xs">{pointsDisplay()}</span>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
